import { useMemo, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft,
  FiPlus,
  FiSettings,
  FiShield,
  FiUserPlus,
  FiUsers,
  FiVideo,
  FiLink,
  FiX,
} from 'react-icons/fi';
import { paths } from '../../../../app/routes/paths';
import { showErrorToast, showSuccessToast } from '../../../../app/components/SnackBar/SnackBar';
import { useAppSelector } from '../../../../app/store/hooks';
import './MeetPage.scss';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

const MeetPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const accessToken = useAppSelector((state) => state.authorization.accessToken);

  const [emails, setEmails] = useState<string[]>([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [title, setTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = useMemo(
    () => emails.length >= 2 && !isSubmitting,
    [emails.length, isSubmitting],
  );

  const addEmail = (raw: string) => {
    const email = normalizeEmail(raw);
    if (!email) return;
    if (!EMAIL_RE.test(email)) {
      showErrorToast(t('meet.invalidEmail'));
      return;
    }
    if (emails.includes(email)) {
      setEmailDraft('');
      return;
    }
    setEmails((prev) => [...prev, email]);
    setEmailDraft('');
  };

  const removeEmail = (email: string) => {
    setEmails((prev) => prev.filter((item) => item !== email));
  };

  const onEmailKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',' || event.key === 'Tab') {
      event.preventDefault();
      addEmail(emailDraft);
    }
    if (event.key === 'Backspace' && !emailDraft && emails.length > 0) {
      removeEmail(emails[emails.length - 1]);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (emails.length < 2) {
      showErrorToast(t('meet.minParticipants'));
      return;
    }
    if (!accessToken) {
      navigate(`${paths.signIn}?next=${encodeURIComponent(paths.meet)}`);
      return;
    }
    setIsSubmitting(true);
    // Backend Meet create for ad-hoc invites lands in a follow-up slice;
    // session create already wires Google Meet when mentoring a lesson.
    window.setTimeout(() => {
      setIsSubmitting(false);
      showSuccessToast(t('meet.createPending'));
    }, 400);
  };

  return (
    <div className="meet-page">
      <div className="meet-page__layout">
        <form className="meet-page__card" onSubmit={handleSubmit}>
          <button
            type="button"
            className="meet-page__back"
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft aria-hidden />
            {t('meet.back')}
          </button>

          <header className="meet-page__header">
            <h1 className="meet-page__title">
              <span className="meet-page__title-icon" aria-hidden>
                <FiVideo />
              </span>
              {t('meet.title')}
            </h1>
            <p className="meet-page__lead">{t('meet.lead')}</p>
          </header>

          <section className="meet-page__section">
            <div className="meet-page__section-head">
              <FiUsers className="meet-page__section-icon" aria-hidden />
              <div>
                <h2 className="meet-page__section-title">
                  {t('meet.participants')}
                </h2>
                <p className="meet-page__section-hint">
                  {t('meet.participantsHint')}
                </p>
              </div>
            </div>
            <div className="meet-page__chips">
              {emails.map((email) => (
                <span key={email} className="meet-page__chip">
                  {email}
                  <button
                    type="button"
                    className="meet-page__chip-remove"
                    aria-label={t('meet.removeEmail', { email })}
                    onClick={() => removeEmail(email)}
                  >
                    <FiX />
                  </button>
                </span>
              ))}
              <div className="meet-page__chip-input-wrap">
                <input
                  className="meet-page__chip-input"
                  type="email"
                  value={emailDraft}
                  placeholder={t('meet.addEmail')}
                  onChange={(event) => setEmailDraft(event.target.value)}
                  onKeyDown={onEmailKeyDown}
                  onBlur={() => addEmail(emailDraft)}
                />
                <button
                  type="button"
                  className="meet-page__chip-add"
                  aria-label={t('meet.addEmail')}
                  onClick={() => addEmail(emailDraft)}
                >
                  <FiPlus />
                </button>
              </div>
            </div>
          </section>

          <section className="meet-page__section">
            <div className="meet-page__section-head">
              <FiSettings className="meet-page__section-icon" aria-hidden />
              <div>
                <h2 className="meet-page__section-title">
                  {t('meet.settings')}
                </h2>
                <p className="meet-page__section-hint">
                  {t('meet.settingsHint')}
                </p>
              </div>
            </div>
            <input
              className="meet-page__input"
              type="text"
              value={title}
              maxLength={120}
              placeholder={t('meet.titlePlaceholder')}
              onChange={(event) => setTitle(event.target.value)}
            />
          </section>

          <button
            type="submit"
            className="meet-page__submit"
            disabled={!canSubmit}
          >
            <span className="meet-page__submit-icon" aria-hidden>
              <FiVideo />
            </span>
            {isSubmitting ? t('meet.creating') : t('meet.createCta')}
          </button>
        </form>

        <aside className="meet-page__aside" aria-label={t('meet.asideTitle')}>
          <div className="meet-page__aside-brand">
            <span className="meet-page__aside-logo" aria-hidden>
              <FiVideo />
            </span>
            <div>
              <p className="meet-page__aside-name">{t('meet.asideTitle')}</p>
              <p className="meet-page__aside-tagline">
                {t('meet.asideTagline')}
              </p>
            </div>
          </div>

          <ul className="meet-page__features">
            <li>
              <span className="meet-page__feature-icon" aria-hidden>
                <FiLink />
              </span>
              <div>
                <strong>{t('meet.featureRoomTitle')}</strong>
                <p>{t('meet.featureRoomBody')}</p>
              </div>
            </li>
            <li>
              <span className="meet-page__feature-icon" aria-hidden>
                <FiUserPlus />
              </span>
              <div>
                <strong>{t('meet.featureInviteTitle')}</strong>
                <p>{t('meet.featureInviteBody')}</p>
              </div>
            </li>
            <li>
              <span className="meet-page__feature-icon" aria-hidden>
                <FiShield />
              </span>
              <div>
                <strong>{t('meet.featureSafeTitle')}</strong>
                <p>{t('meet.featureSafeBody')}</p>
              </div>
            </li>
          </ul>

          <p className="meet-page__aside-note">
            <FiVideo aria-hidden />
            {t('meet.asideNote')}
          </p>
        </aside>
      </div>
    </div>
  );
};

export default MeetPage;
